import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { PHASES } from "./data";
import { LANDING } from "@/constants/testIds";

function PhaseCard({ p, i, mobileCarousel = false }) {
  return (
    <motion.div
      data-testid={LANDING.methodPhaseCard}
      initial={mobileCarousel ? false : { opacity: 0, y: 30 }}
      whileInView={mobileCarousel ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (i % 2) * 0.1 }}
      whileHover={mobileCarousel ? undefined : { y: -4 }}
      className={`h-full rounded-3xl overflow-hidden border border-white/10 hover:border-violet-400/30 transition-all duration-500 bg-[#3d2a5a] flex flex-col p-7 lg:p-8 ${
        mobileCarousel ? "" : "group"
      }`}
    >
      {/* Phase badge — substitui a antiga coluna lateral */}
      <span className="self-start inline-flex items-center gap-2 rounded-full border border-[#c4b5fd]/35 bg-[#c4b5fd]/10 px-3.5 py-1.5 mb-5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#c4b5fd]" />
        <span className="text-[11px] tracking-[0.24em] text-[#c4b5fd] font-bold uppercase leading-none">
          Fase {p.n}
        </span>
      </span>

      {/* Título — largura total disponível */}
      <h3 className="font-display text-[22px] sm:text-2xl lg:text-[26px] text-violet-100 leading-[1.2] mb-6 text-balance">
        {p.title}
      </h3>

      {/* Bullets — texto respira em linhas confortáveis */}
      <ul className="space-y-3.5">
        {p.bullets.map((b, idx) => (
          <li
            key={idx}
            className="flex gap-3 text-slate-200/90 text-[14.5px] lg:text-[15px] leading-[1.6]"
          >
            <Check
              className="w-4 h-4 text-[#a78bfa] shrink-0 mt-[5px]"
              strokeWidth={2.5}
            />
            <span className="flex-1 text-pretty">{b}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function MethodSection() {
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Track which card is centered to update the indicator dots.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const center = el.scrollLeft + el.clientWidth / 2;
      const slides = el.querySelectorAll("[data-slide]");
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((slide, idx) => {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        const d = Math.abs(slideCenter - center);
        if (d < bestDist) {
          bestDist = d;
          best = idx;
        }
      });
      setActiveIndex(best);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const goToSlide = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const slide = el.querySelectorAll("[data-slide]")[idx];
    if (slide) {
      el.scrollTo({
        left:
          slide.offsetLeft - (el.clientWidth - slide.clientWidth) / 2,
        behavior: "smooth",
      });
    }
  };

  return (
    <section
      id="metodologia"
      className="py-28 lg:py-36 bg-[#2d1f43] relative overflow-hidden"
    >
      <div className="absolute inset-0 stars-bg opacity-25 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-4xl mx-auto mb-12 lg:mb-16"
        >
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[52px] text-white leading-[1.1] mb-7">
            Por que a maioria tenta por meses sem resultado.
            <span className="block text-[#a78bfa] font-medium mt-3">
              E o que muda quando existe um protocolo na ordem certa.
            </span>
          </h2>
          <p className="text-slate-200/85 text-base lg:text-lg leading-[1.75] max-w-2xl mx-auto">
            Sono infantil tem uma lógica. Quando você pula etapas, o resultado
            não aparece. A{" "}
            <span className="text-violet-200 font-medium">Jornada do Sono</span>{" "}
            conduz você desde a compreensão do sono até noites previsíveis,
            respeitando o desenvolvimento de cada criança.
          </p>
        </motion.div>

        {/* DESKTOP / TABLET grid — unchanged */}
        <div className="hidden md:grid md:grid-cols-2 gap-5 lg:gap-6 items-stretch">
          {PHASES.map((p, i) => (
            <PhaseCard key={p.n} p={p} i={i} />
          ))}
        </div>

        {/* MOBILE carousel — horizontal snap scroll with peek + indicator */}
        <div className="md:hidden -mx-6">
          <div
            ref={scrollerRef}
            data-testid="method-mobile-carousel"
            role="region"
            aria-label="Carrossel das fases do método"
            tabIndex={0}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth px-6 pb-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            style={{
              scrollPaddingLeft: "1.5rem",
              scrollPaddingRight: "1.5rem",
              overscrollBehaviorX: "contain",
            }}
          >
            {PHASES.map((p, i) => (
              <div
                key={p.n}
                data-slide
                aria-roledescription="slide"
                aria-label={`Fase ${p.n} de ${PHASES.length}`}
                className="snap-center shrink-0 w-[86%] max-w-[340px] first:ml-[7%] last:mr-[7%]"
              >
                <PhaseCard p={p} i={i} mobileCarousel />
              </div>
            ))}
          </div>

          {/* Swipe hint + dot indicator */}
          <div className="mt-6 flex flex-col items-center gap-3">
            <div
              className="flex items-center gap-1.5"
              role="tablist"
              aria-label="Selecionar fase"
            >
              {PHASES.map((p, idx) => {
                const active = idx === activeIndex;
                return (
                  <button
                    key={p.n}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-label={`Ir para Fase ${p.n}`}
                    onClick={() => goToSlide(idx)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      active
                        ? "w-7 bg-[#c4b5fd]"
                        : "w-1.5 bg-white/25 hover:bg-white/40"
                    }`}
                  />
                );
              })}
            </div>
            <p className="text-[11px] tracking-[0.22em] uppercase text-[#a78bfa]/80 font-bold flex items-center gap-2">
              <span aria-hidden="true">←</span>
              Arraste para ver as 6 fases
              <span aria-hidden="true">→</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
